import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { useTranslation } from 'react-i18next';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../theme'; // Ensure this path is correct
import { getFirestore, collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for redirection
const industries = [
    'Automotive', 'Auto parts', 'Gifts and promotional items', 'Electronics',
    'Fashion', 'Food and beverages', 'Furniture', 'Health and beauty',
    'Home appliances', 'Books and stationery', 'Sports and leisure', 'Other'
];
const storageAreas = [
    '2m2', '5m2', '10m2', '20m2', '50m2', '100m2', '3_5m2', '5_10m2'
];
const openingHoursDays = [
    'monday_friday', 'monday_saturday', 'monday_sunday'
];
const contractedToProviders = [
    'Express One', 'DPD', 'GLS', 'Sprinter', 'TNT', 'DHL', 'UPS', 'Other'
];
const CheckpointOnboarding = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        company_name: '',
        post_code: '',
        industry: '',
        street: '',
        street_number: '',
        city: '',
        website: '',
        has_parcelshop: '',
        contracted_to: '',
        storage_area: '',
        opening_hours_days: '',
        opening_hours_hours: '',
        photos: '',
        notes: '',
    });
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };
    const handleHasParcelShopChange = (event) => {
        const value = event.target.value;
        setFormData((prevData) => ({
            ...prevData,
            has_parcelshop: value,
            contracted_to: value === 'Yes' ? prevData.contracted_to : '', // Clear contracted_to if no parcel shop
        }));
    };
    const validateForm = () => {
        const { company_name, post_code, city, street, street_number, website, industry, has_parcelshop, contracted_to, storage_area, opening_hours_days } = formData;
        if (!company_name || !post_code || !city || !street || !street_number || !website || !industry ||
            !has_parcelshop || (has_parcelshop === 'Yes' && !contracted_to) || !storage_area ||
            !opening_hours_days) {
            return t('form_incomplete');
        }
        return null;
    };
    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) {
                throw new Error(t('user_not_authenticated'));
            }
            const userId = user.uid;
            const db = getFirestore();
            // Retrieve user document
            const userDocRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userDocRef);
            if (!userDoc.exists()) {
                throw new Error(t('user_document_not_found'));
            }
            const userData = userDoc.data();
            // Save onboarding data
            await addDoc(collection(db, 'onboarding'), {
                ...formData,
                userId,
                email: userData.email,
                createdAt: new Date(),
            });
            // Update user document to mark onboarding as complete
            await updateDoc(userDocRef, { onboardingComplete: true });
            alert(t('submission_success'));
            // Redirect to dashboard based on user role
            const role = userData.role;
            if (role === 'checkpoint') {
                navigate('/checkpoint-dashboard');
            }
            else {
                navigate('/');
            }
            // Clear form data
            setFormData({
                company_name: '',
                post_code: '',
                industry: '',
                street: '',
                street_number: '',
                city: '',
                website: '',
                has_parcelshop: '',
                contracted_to: '',
                storage_area: '',
                opening_hours_days: '',
                opening_hours_hours: '',
                photos: '',
                notes: '',
            });
        }
        catch (error) {
            console.error('Error adding document:', error);
            setError(t('submission_error'));
        }
    };
    return (_jsx(ThemeProvider, { theme: theme, children: _jsxs(Container, { component: "main", maxWidth: "xs", children: [_jsx(CssBaseline, {}), _jsxs(Box, { sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        mx: 'auto',
                    }, children: [_jsx(Avatar, { sx: { m: 1, bgcolor: 'secondary.main' }, children: _jsx(LockOutlinedIcon, {}) }), _jsx(Typography, { component: "h1", variant: "h5", sx: { mb: 2 }, children: t('checkpoint_onboarding') }), _jsxs(Box, { component: "form", noValidate: true, onSubmit: handleSubmit, sx: { width: '70%', mx: 'auto' }, children: [_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "company_name", label: t('company_name'), value: formData.company_name, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "post_code", label: t('post_code'), value: formData.post_code, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { select: true, fullWidth: true, name: "industry", label: t('industry'), value: formData.industry, onChange: handleChange, children: industries.map((industry) => (_jsx(MenuItem, { value: industry, children: t(`industries.${industry}`) }, industry))) }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "street", label: t('street'), value: formData.street, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "street_number", label: t('street_number'), value: formData.street_number, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "city", label: t('city'), value: formData.city, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "website", label: t('website'), value: formData.website, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsxs(TextField, { select: true, fullWidth: true, name: "has_parcelshop", label: t('has_parcelshop'), value: formData.has_parcelshop, onChange: handleHasParcelShopChange, children: [_jsx(MenuItem, { value: "Yes", children: t('yes') }), _jsx(MenuItem, { value: "No", children: t('no') })] }) }), formData.has_parcelshop === 'Yes' && (_jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { select: true, fullWidth: true, name: "contracted_to", label: t('contracted_to'), value: formData.contracted_to, onChange: handleChange, children: contractedToProviders.map((provider) => (_jsxs(MenuItem, { value: provider, children: [provider, " "] }, provider))) }) })), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { select: true, fullWidth: true, name: "storage_area", label: t('storage_area'), value: formData.storage_area, onChange: handleChange, children: storageAreas.map((area) => (_jsx(MenuItem, { value: area, children: t(`storage_area_values.${area}`) }, area))) }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { select: true, fullWidth: true, name: "opening_hours_days", label: t('opening_hours_days'), value: formData.opening_hours_days, onChange: handleChange, children: openingHoursDays.map((day) => (_jsx(MenuItem, { value: day, children: t(`opening_hours_days_values.${day}`) }, day))) }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "opening_hours_hours", label: t('opening_hours_hours'), value: formData.opening_hours_hours, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "photos", label: t('provide_photo_url'), value: formData.photos, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "notes", label: t('notes'), value: formData.notes, onChange: handleChange }) })] }), error && (_jsx(Typography, { color: "error", sx: { mt: 2 }, children: error })), _jsx(Button, { type: "submit", fullWidth: true, variant: "contained", sx: { mt: 3, mb: 2 }, children: t('submit') })] })] })] }) }));
};
export default CheckpointOnboarding;
